import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Loader2, Activity, CheckCircle2, AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = window.location.origin + "/api";

const fmtEUR = (n) =>
  Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit", month: "2-digit", year: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

/**
 * Pannello admin: progresso del cleanup AUTOMATICO (read-time audit).
 * Mostra quante opp legacy con prezzi BDD-sporchi sono state auto-pulite
 * silenziosamente all'apertura nelle ultime N giorni, e una stima di
 * quante ne restano in attesa di essere aperte.
 *
 * Complementare a `ListinoPriceAuditPanel` (cleanup manuale batch).
 */
export default function AutoHealStatsPanel() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [lastFetchedAt, setLastFetchedAt] = useState(null);

  const fetchStats = useCallback(async (manual = false) => {
    setLoading(true);
    // Min 350ms così lo spinner è sempre visibile anche se la API è veloce
    const startedAt = Date.now();
    try {
      const { data: resp } = await axios.get(`${API}/admin/audit-stats?days=7`, {
        withCredentials: true,
      });
      const elapsed = Date.now() - startedAt;
      if (elapsed < 350) await new Promise(r => setTimeout(r, 350 - elapsed));
      setData(resp);
      setLastFetchedAt(new Date());
      if (manual) {
        toast.success(
          `Stats aggiornate: ${resp.healed_recent.count} pulite (7gg) · ${resp.pending_estimate.count} pending`,
          { duration: 3000 },
        );
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore caricamento stats");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStats(false); }, [fetchStats]);

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4 space-y-3" data-testid="autoheal-stats-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
            <Activity className="w-4 h-4 text-emerald-600" />
          </div>
          <div>
            <div className="text-sm font-bold text-slate-800">Auto-heal prezzi</div>
            <div className="text-[10px] text-slate-500">Cleanup automatico legacy prices</div>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => fetchStats(true)}
          disabled={loading}
          className="h-7 px-2"
          data-testid="autoheal-refresh-btn"
          title="Aggiorna stats"
        >
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
        </Button>
      </div>

      {!data && loading && (
        <div className="text-xs text-slate-500 flex items-center gap-2">
          <Loader2 className="w-3 h-3 animate-spin" />
          Caricamento…
        </div>
      )}

      {data && (
        <div className="space-y-3 text-xs">
          {/* Healed recente */}
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-2">
              <div className="text-emerald-700 font-bold text-base flex items-center gap-1">
                <CheckCircle2 className="w-4 h-4" />
                {data.healed_recent.count}
              </div>
              <div className="text-[10px] text-emerald-800">
                Auto-pulite ultimi {data.window_days}gg
              </div>
            </div>
            <div className={`${data.pending_estimate.count > 0 ? "bg-amber-50 border-amber-200" : "bg-slate-50 border-slate-200"} border rounded-lg p-2`}>
              <div className={`${data.pending_estimate.count > 0 ? "text-amber-700" : "text-slate-700"} font-bold text-base flex items-center gap-1`}>
                {data.pending_estimate.count > 0 ? <AlertTriangle className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
                {data.pending_estimate.count}
              </div>
              <div className={`text-[10px] ${data.pending_estimate.count > 0 ? "text-amber-800" : "text-slate-600"}`}>
                Ancora da pulire (stima)
              </div>
            </div>
          </div>

          <div className="text-[10px] text-slate-500 flex items-center justify-between">
            <span>
              Totale opp mai auto-pulite: <span className="font-semibold">{data.total_healed}</span>
              {" · "}
              scansionate {data.pending_estimate.scanned}/{data.pending_estimate.scan_limit}
            </span>
            {lastFetchedAt && (
              <span className="text-slate-400" title={lastFetchedAt.toISOString()}>
                aggiornato {fmtDate(lastFetchedAt.toISOString())}
              </span>
            )}
          </div>

          {/* Sample healed */}
          {data.healed_recent.sample?.length > 0 && (
            <details className="text-[11px]">
              <summary className="cursor-pointer text-emerald-700 hover:text-emerald-900">
                Ultime {data.healed_recent.sample.length} opp auto-pulite ▾
              </summary>
              <div className="mt-2 max-h-40 overflow-y-auto border border-slate-200 rounded">
                {data.healed_recent.sample.map((o) => (
                  <div key={o.opportunity_id} className="px-2 py-1 border-b border-slate-100 last:border-0 flex justify-between gap-2 text-[10px]">
                    <div className="truncate">
                      <span className="font-semibold">{o.contact_name || "—"}</span>
                      <div className="text-slate-500 truncate">{o.assicurazione_name} · {fmtDate(o.audit_self_healed_at)}</div>
                    </div>
                    <div className="text-right whitespace-nowrap">
                      <div className="font-mono text-emerald-700">€{fmtEUR(o.total_cost)}</div>
                      <div className="text-slate-400 text-[9px]">{o.audit_self_healed_count} righe</div>
                    </div>
                  </div>
                ))}
              </div>
            </details>
          )}

          {/* Sample pending */}
          {data.pending_estimate.sample?.length > 0 && (
            <details className="text-[11px]">
              <summary className="cursor-pointer text-amber-700 hover:text-amber-900">
                {data.pending_estimate.sample.length} opp da pulire (sample) ▾
              </summary>
              <div className="mt-2 max-h-40 overflow-y-auto border border-slate-200 rounded">
                {data.pending_estimate.sample.map((o) => (
                  <div key={o.opportunity_id} className="px-2 py-1 border-b border-slate-100 last:border-0 flex justify-between gap-2 text-[10px]">
                    <div className="truncate">
                      <span className="font-semibold">{o.contact_name || "—"}</span>
                      <div className="text-slate-500 truncate">{o.assicurazione_name}</div>
                    </div>
                    <div className="text-right whitespace-nowrap">
                      <div className="font-mono text-amber-700">€{fmtEUR(o.current_total_cost)}</div>
                      <div className="text-slate-400 text-[9px]">{o.rows_with_bug_signature} righe</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="text-[10px] text-slate-500 mt-1 italic">
                Si auto-puliranno alla prossima apertura. Per cleanup immediato batch, usa il pannello &quot;Audit prezzi listino&quot; qui sopra.
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
}
